import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSavedDesigns } from "@/context/SavedDesignsContext";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { LogOut, LayoutGrid, Package, Menu, X, Users, HardDrive, User, Heart, ShieldAlert, Eye, BookUser, Link as LinkIcon } from "lucide-react";
import StorageWarningBanner from "@/components/StorageWarningBanner";
import PwaInstallBanner from "@/components/PwaInstallBanner";
import InstallAppMenuItem from "@/components/InstallAppMenuItem";
import { useActivityTracker, useDevtoolsDetector } from "@/hooks/useActivityTracker";
import { useServiceWorkerAutoUpdate } from "@/hooks/useServiceWorkerAutoUpdate";
import { useVersionPin } from "@/hooks/useVersionPin";
import { usePresenceHeartbeat } from "@/hooks/usePresenceHeartbeat";
import { AppLogo } from "@/components/AppLogo";
import BrandingUpdateBanner from "@/components/BrandingUpdateBanner";
import { PriceVisibilityToggle, PresentationModeBanner } from "@/components/PriceVisibilityToggle";

const NavItem = ({ to, icon: Icon, label, badge, testId }) => (
  <NavLink
    to={to}
    data-testid={testId}
    className={({ isActive }) =>
      `flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
        isActive
          ? "text-[#D62828] border-b-2 border-[#F4A100]"
          : "text-[#4A4A4A] hover:text-[#D62828]"
      }`
    }
  >
    <Icon size={16} strokeWidth={1.5} />
    <span>{label}</span>
    {badge > 0 && (
      <span className="ml-1 inline-flex items-center justify-center bg-[#D62828] text-white text-[10px] font-medium h-4 min-w-4 px-1 rounded-full">
        {badge}
      </span>
    )}
  </NavLink>
);

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pending, setPending] = useState(0);
  const [alertCount, setAlertCount] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [headerHidden, setHeaderHidden] = useState(false);

  const isAdmin = user?.role === "admin";

  // Wire global devtools detection — fires once per session if a trader opens
  // the browser inspector while logged in. Admins are no-ops on the backend.
  const track = useActivityTracker();
  useDevtoolsDetector(() => track("DEVTOOLS_OPEN"));
  // Silent PWA self-heal: when a new Service Worker takes control (after a deploy),
  // reload the tab once so the user instantly gets the fresh JS bundle.
  useServiceWorkerAutoUpdate();
  // Remote kill-switch — if admin clicked "Flush stale PWA clients" and our
  // BUILD_VERSION is below the server-pinned minimum, wipe everything and
  // hard-reload. Reads /api/version on every Layout mount.
  useVersionPin(process.env.REACT_APP_BACKEND_URL);
  // Live-presence heartbeat — 30 s ping while the tab is visible, so the
  // admin dashboard shows real-time "X people on the site right now".
  usePresenceHeartbeat(user);

  // One scroll listener powers both the progress bar AND mobile-only header auto-hide.
  // While the mobile menu drawer is open we suspend auto-hide entirely — otherwise
  // the user opens the menu, the next scroll-listener tick reads y > HIDE_AFTER_PX
  // and instantly re-hides the header (and the drawer inside it).
  useEffect(() => {
    let rafId = 0;
    const TOP_REVEAL_PX = 24;
    const HIDE_AFTER_PX = 60;
    const update = () => {
      rafId = 0;
      const h = document.documentElement;
      const y = h.scrollTop;
      const max = (h.scrollHeight - h.clientHeight) || 1;
      setScrollProgress(Math.min(1, Math.max(0, y / max)));

      const isMobile = window.matchMedia("(max-width: 767px)").matches;
      if (!isMobile || menuOpen) {
        if (!isMobile) setHeaderHidden(false);
        return;
      }
      if (y < TOP_REVEAL_PX) setHeaderHidden(false);
      else if (y > HIDE_AFTER_PX) setHeaderHidden(true);
    };
    const onScroll = () => { if (!rafId) rafId = requestAnimationFrame(update); };
    window.addEventListener("scroll", onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [menuOpen]);

  // Expose the current effective header height as a CSS variable so any sticky element
  // below the header (e.g. CatalogPage's filter bar) can adjust its `top` to slide
  // with the header as it appears/disappears.
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--app-header-offset",
      headerHidden ? "0px" : "44px"
    );
  }, [headerHidden]);

  useEffect(() => {
    if (!isAdmin) return;
    let active = true;
    const tick = () => {
      api.get("/admin/stats")
        .then(({ data }) => { if (active) setPending(data.pending_customers || 0); })
        .catch(() => {});
      api.get("/admin/alerts/unack-count")
        .then(({ data }) => { if (active) setAlertCount(data.count || 0); })
        .catch(() => {});
    };
    tick();
    const id = setInterval(tick, 12000);
    return () => { active = false; clearInterval(id); };
  }, [isAdmin]);

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex flex-col">
      <BrandingUpdateBanner />
      <div
        className="sv-scroll-progress"
        style={{ transform: `scaleX(${scrollProgress})` }}
        data-testid="scroll-progress"
        aria-hidden="true"
      />
      <header
        className={`sticky top-0 z-40 bg-[#FAF9F6]/85 backdrop-blur-lg border-b border-[#E5E0D8] transition-transform duration-300 ease-out ${headerHidden ? "-translate-y-full md:translate-y-0" : "translate-y-0"}`}
        data-testid="app-header"
        data-header-hidden={headerHidden ? "true" : "false"}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between h-11 md:h-16">
            <Link to={user ? (isAdmin ? "/admin" : "/catalog") : "/"} className="flex items-center gap-2 md:gap-3 min-w-0" data-testid="brand-link">
              <div className="w-7 h-7 md:w-9 md:h-9 shrink-0 border border-[#F4A100] flex items-center justify-center bg-white" data-testid="brand-logo">
                <AppLogo size="xs" className="md:!w-7 md:!h-7" alt="Paper Kraft Cards logo" testId="brand-logo-img" />
              </div>
              <div className="leading-tight min-w-0">
                <div className="font-serif text-sm md:text-xl text-[#D62828] truncate">Shubh Vivah</div>
                <div className="tracking-luxe text-[10px] text-[#4A4A4A] hidden sm:block">Wedding Card Atelier</div>
                <div className="text-[9px] md:text-[11px] font-semibold tracking-[0.14em] md:tracking-[0.18em] text-[#F4A100] md:mt-0.5">by PAPER KRAFT</div>
              </div>
            </Link>

            {user && (
              <>
                <nav className="hidden md:flex items-center gap-1">
                  {isAdmin ? (
                    <>
                      <NavItem to="/admin" icon={LayoutGrid} label="Dashboard" testId="nav-admin-dashboard" />
                      <NavItem to="/admin/cards" icon={Package} label="Catalog" testId="nav-admin-cards" />
                      <NavItem to="/admin/customers" icon={Users} label="Customers" badge={pending} testId="nav-admin-customers" />
                      <NavItem to="/admin/alerts" icon={ShieldAlert} label="Alerts" badge={alertCount} testId="nav-admin-alerts" />
                      <NavItem to="/admin/visitors" icon={Eye} label="Visitors" testId="nav-admin-visitors" />
                      <NavItem to="/admin/contacts" icon={BookUser} label="Contacts" testId="nav-admin-contacts" />
                      <NavItem to="/admin/share-links" icon={LinkIcon} label="Share Links" testId="nav-admin-share-links" />
                      <NavItem to="/admin/storage" icon={HardDrive} label="Storage" testId="nav-admin-storage" />
                    </>
                  ) : user.status === "approved" ? (
                    <>
                      <NavItem to="/catalog" icon={LayoutGrid} label="Catalog" testId="nav-catalog" />
                    </>
                  ) : null}
                </nav>

                <div className="hidden md:flex items-center gap-3">
                  {!isAdmin && user.status === "approved" && (
                    <PriceVisibilityToggle variant="compact" />
                  )}
                  <div className="text-right leading-tight">
                    <div className="text-sm text-[#1A1A1A]" data-testid="user-name">{user.name}</div>
                    <div className="tracking-luxe text-[10px] text-[#F4A100]">{isAdmin ? "Admin" : (user.status === "approved" ? "Customer" : "Awaiting access")}</div>
                  </div>
                  <button
                    onClick={() => { logout(); nav("/login"); }}
                    className="p-2 text-[#4A4A4A] hover:text-[#D62828] transition-colors"
                    title="Sign out"
                    data-testid="logout-button"
                  >
                    <LogOut size={18} strokeWidth={1.5} />
                  </button>
                </div>

                <button
                  className="md:hidden p-2 text-[#4A4A4A]"
                  onClick={() => setMenuOpen((v) => !v)}
                  data-testid="mobile-menu-toggle"
                >
                  {menuOpen ? <X size={22} /> : <Menu size={22} />}
                </button>
              </>
            )}

            {!user && (
              <div className="flex items-center gap-2 md:gap-3 shrink-0">
                {/* Mobile: tiny single icon to /login (saves ~80% of the auth strip width).
                    Desktop (md+): full Sign in / Create account pair. */}
                <Link
                  to="/login"
                  data-testid="nav-login-mobile"
                  className="md:hidden inline-flex items-center justify-center w-9 h-9 border border-[#F4A100] text-[#D62828] hover:bg-[#F5F2EA] transition-colors"
                  title="Sign in"
                  aria-label="Sign in"
                >
                  <User size={16} strokeWidth={1.75} />
                </Link>
                <Link to="/login" className="hidden md:inline text-sm text-[#4A4A4A] hover:text-[#D62828] whitespace-nowrap" data-testid="nav-login">Sign in</Link>
                <Link
                  to="/register"
                  className="hidden md:inline bg-[#D62828] hover:bg-[#A41C1C] text-white text-sm px-4 py-2 rounded-none transition-colors whitespace-nowrap"
                  data-testid="nav-register"
                >
                  Create account
                </Link>
              </div>
            )}
          </div>

          {user && menuOpen && (
            <div className="md:hidden pb-4 flex flex-col gap-1 border-t border-[#E5E0D8] pt-3" data-testid="mobile-menu">
              {isAdmin ? (
                <>
                  <NavItem to="/admin" icon={LayoutGrid} label="Dashboard" testId="m-nav-admin" />
                  <NavItem to="/admin/cards" icon={Package} label="Catalog" testId="m-nav-cards" />
                  <NavItem to="/admin/customers" icon={Users} label="Customers" badge={pending} testId="m-nav-customers" />
                  <NavItem to="/admin/alerts" icon={ShieldAlert} label="Alerts" badge={alertCount} testId="m-nav-alerts" />
                  <NavItem to="/admin/visitors" icon={Eye} label="Visitors" testId="m-nav-visitors" />
                  <NavItem to="/admin/contacts" icon={BookUser} label="Contacts" testId="m-nav-contacts" />
                  <NavItem to="/admin/share-links" icon={LinkIcon} label="Share Links" testId="m-nav-share-links" />
                  <NavItem to="/admin/storage" icon={HardDrive} label="Storage" testId="m-nav-storage" />
                </>
              ) : user.status === "approved" ? (
                <>
                  <NavItem to="/catalog" icon={LayoutGrid} label="Catalog" testId="m-nav-catalog" />
                  <PriceVisibilityToggle variant="inline" />
                </>
              ) : null}
              <InstallAppMenuItem variant="mobile" onAfterClick={() => setMenuOpen(false)} />
              <button
                onClick={() => { logout(); nav("/login"); }}
                className="text-left px-3 py-2 text-sm text-[#D62828]"
                data-testid="m-logout"
              >
                Sign out ({user.name})
              </button>
            </div>
          )}
        </div>
      </header>

      {isAdmin && <StorageWarningBanner />}
      {!isAdmin && user?.status === "approved" && <PresentationModeBanner />}

      <main className="flex-1">{children}</main>

      {/* Floating "Saved" pill — pinned bottom-right on every page (except /saved itself).
          Visible only when the customer has at least one design in their wishlist. */}
      <SavedFloatingPill />

      {/* Floating menu trigger — only renders on mobile when the auto-hidden
          header is off-screen. Lets the trader open the nav drawer on demand
          without scrolling back to the top of the page. Tapping it ALSO
          reveals the header (the drawer lives inside the header element). */}
      {user && headerHidden && !menuOpen && (
        <button
          type="button"
          onClick={() => { setHeaderHidden(false); setMenuOpen(true); }}
          data-testid="floating-menu-button"
          aria-label="Open menu"
          className="md:hidden fixed top-3 right-3 z-50 w-10 h-10 bg-[#D62828] text-white shadow-lg rounded-full flex items-center justify-center active:scale-95 transition-transform"
        >
          <Menu size={18} strokeWidth={2} />
        </button>
      )}

      {/* Persistent "Install app" banner — visible site-wide for non-admin visitors
          (logged-out browsers, pending traders and approved customers). Admins
          don't see it because they manage the catalog from desktop. The banner
          decides for itself whether to show based on `display-mode: standalone`
          and a 1-hour dismiss cooldown. */}
      {!isAdmin && <PwaInstallBanner />}

      <footer className="border-t border-[#E5E0D8] bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="tracking-luxe text-[10px] text-[#4A4A4A]">© Shubh Vivah Atelier · <span className="font-semibold text-[#F4A100] tracking-[0.18em]">PAPER KRAFT</span></div>
          <div className="flex items-center gap-5">
            <Link
              to="/contact"
              data-testid="footer-contact-link"
              className="tracking-luxe text-[10px] text-[#4A4A4A] hover:text-[#D62828] transition-colors"
            >
              Contact &amp; About
            </Link>
            <div className="font-serif text-sm text-[#4A4A4A] hidden md:block">Crafted for India&apos;s most beautiful weddings</div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Bottom-right floating pill that links to the /saved wishlist page. Hides when the
// wishlist is empty AND when the user is already on /saved (so it doesn't overlap the
// big "Send all to WhatsApp" CTA at the bottom of that page).
function SavedFloatingPill() {
  const { count } = useSavedDesigns();
  const onSavedPage = typeof window !== "undefined" && window.location.pathname === "/saved";
  if (count <= 0 || onSavedPage) return null;
  return (
    <Link
      to="/saved"
      data-testid="floating-saved-pill"
      className="fixed bottom-5 right-5 z-50 inline-flex items-center gap-2 bg-[#D62828] hover:bg-[#A41C1C] text-white px-4 py-3 shadow-lg rounded-full transition-colors animate-[sv-fade-up_0.3s_ease-out]"
      aria-label={`View ${count} saved design${count === 1 ? "" : "s"}`}
    >
      <Heart size={16} fill="#F4A100" stroke="#F4A100" strokeWidth={1.5} />
      <span className="tracking-luxe text-[11px]">Saved</span>
      <span className="bg-[#F4A100] text-[#D62828] font-semibold text-[11px] px-2 py-0.5 rounded-full min-w-[22px] text-center">
        {count}
      </span>
    </Link>
  );
}

